// src/components/AllChats.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import audioFile from '../assets/audio/ping.mp3';
import Pagination from './Pagination';

const AllChats = ({ onChatClick, searchQuery, chats, setChats, onChatRead }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [newMessageMap, setNewMessageMap] = useState({});
  const messageSoundRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem('accessToken');

  // ✅ Fetch chats
  const fetchChats = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
      };

      // Add search query if provided
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await axios.get(`${API_BASE_URL}/support-chat/all`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.data && response.data.pagination) {
        // New paginated format
        setChats(response.data.data);
        setPagination(response.data.pagination);
      } else if (Array.isArray(response.data.chats)) {
        // Old format fallback
        setChats(response.data.chats);
        setPagination(null);
      } else if (Array.isArray(response.data)) {
        // Direct array fallback
        setChats(response.data);
        setPagination(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('❌ Error fetching chats:', err);
      setError('Failed to fetch chats');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, setChats, searchQuery]);

  // ✅ Incoming socket message
  const handleIncomingSocketMessage = useCallback((event) => {
    const { chatId } = event.detail || {};
    if (!chatId) return;

    setNewMessageMap(prev => ({
      ...prev,
      [chatId]: true,
    }));

    try {
      const sound = messageSoundRef.current;
      if (sound) {
        sound.pause();
        sound.currentTime = 0;
        sound.play();
      }
    } catch (err) {
      console.warn('🔇 Sound failed:', err);
    }

    fetchChats();
  }, [fetchChats]);

  useEffect(() => {
    window.addEventListener('chat-message', handleIncomingSocketMessage);
    return () => {
      window.removeEventListener('chat-message', handleIncomingSocketMessage);
    };
  }, [handleIncomingSocketMessage]);

  useEffect(() => {
    messageSoundRef.current = new Audio(audioFile);
    messageSoundRef.current.load();
  }, []);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
    fetchChats(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchChats(page);
  };

  const handleChatClick = (chat) => {
    setNewMessageMap(prev => ({
      ...prev,
      [chat.id]: false,
    }));

    onChatRead?.(chat.id);
    onChatClick(chat);
  };

  useEffect(() => {
    const handleChatRead = (event) => {
      const { chatId } = event.detail || {};
      if (!chatId) return;

      setNewMessageMap(prev => ({
        ...prev,
        [chatId]: false,
      }));
    };

    window.addEventListener('chat-read', handleChatRead);
    return () => {
      window.removeEventListener('chat-read', handleChatRead);
    };
  }, []);

  if (loading) return <p>Loading chats...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div>
      <table cellPadding="8" cellSpacing="0" style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
        <thead style={{ backgroundColor: 'rgb(250, 248, 255)', borderTop: '1px solid rgb(233, 230, 241)', borderBottom: '1px solid rgb(233, 230, 241)' }}>
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Messages</th>
            <th>Responded</th>
            <th>Seen</th>
            <th>Last Updated</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {chats.length === 0 ? (
            <tr>
              <td colSpan="7" className="p-2 text-center">No chats found</td>
            </tr>
          ) : (
            chats.map(chat => (
              <tr key={chat.id}>
                <td className="p-2 text-center">{chat.user?.fullName || 'N/A'}</td>
                <td className="p-2 text-center">{chat.user?.email || 'N/A'}</td>
                <td className="p-2 text-center">
                  {chat.messages?.length || 0}
                  {newMessageMap?.[chat.id] && <span style={{ color: 'green' }}> 🟢 New</span>}
                </td>
                <td className="p-2 text-center">{chat.responded ? 'Yes' : 'No'}</td>
                <td className="p-2 text-center">{chat.seen ? 'Yes' : 'No'}</td>
                <td className="p-2 text-center">{new Date(chat.updatedAt).toLocaleString()}</td>
                <td className="p-2 text-center">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleChatClick(chat)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      
      {pagination && (
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      )}
    </div>
  );
};

export default AllChats;
